// This is needed in order to sync routers from react-router in different web-components
// Without it for example routers in other web-components would not know about
// url changes made by clicking Link components in the main app menu
import { createBrowserHistory } from 'history'
import type { Blocker } from 'history'

const history = createBrowserHistory()
let isURLChangeStartedByEmberRouter = false

const originalBlock = history.block.bind(history)

// history v5 registers a `beforeunload` prompt together with the first blocker, and the Ember bridge
// (useReactEmberRouterConnection) keeps a blocker registered for the whole session, which would make the browser
// ask for confirmation on every reload. Transitions are blocked in push/replace/popstate, not in `beforeunload`,
// so skipping that one registration keeps blocking intact and only drops the prompt.
history.block = (blocker: Blocker) => {
  const originalAddEventListener = window.addEventListener

  window.addEventListener = ((...args: Parameters<typeof originalAddEventListener>) => {
    if (args[0] === 'beforeunload') {
      return
    }

    originalAddEventListener.apply(window, args)
  }) as typeof window.addEventListener

  try {
    return originalBlock(blocker)
  } finally {
    window.addEventListener = originalAddEventListener
  }
}

export interface EmberHistoryEventDetail {
  url: string
}

// We are using this object to trick Ember's router to do any routing via react-router
// It is used in application.js
window._sharedHistory = {
  pushState: function (data: any, unused: string, url?: string | URL | null) {
    if (url) {
      isURLChangeStartedByEmberRouter = true
      history.push(url.toString(), data)
      isURLChangeStartedByEmberRouter = false
    }
  },
  replaceState: function (data: any, unused: string, url?: string | URL | null) {
    if (url) {
      isURLChangeStartedByEmberRouter = true
      history.replace(url.toString(), data)
      isURLChangeStartedByEmberRouter = false
    }
  },
}

const getIsURLChangeStartedByEmberRouter = () => isURLChangeStartedByEmberRouter
const unsafeReplaceHistoryState = (newPath: string, state: any = null): void => {
  window._sharedHistory.replaceState(state, '', newPath)
}

export { history, getIsURLChangeStartedByEmberRouter, unsafeReplaceHistoryState }
